// "Process remaining pages" in batches.
//
// The backend OCRs at most CONTINUE_OCR_MAX_PAGES pages per request (memory
// review T5: one request used to OCR every remaining page of a 200-page record
// and could exhaust the server's memory). Each response says whether more
// pages remain (`has_more`, `remaining_pages`); this keeps calling until none
// do, and folds the batches into one result shaped like the old single call.
//
// `requestBatch` is the caller's own single request (it keeps its own fetch,
// error mapping and messages) and must resolve to the parsed JSON body.
// `onProgress({ pagesProcessed, totalPages, remainingPages, requests })` fires
// after every batch.

// Far above any real document (10 pages a request); only a guard against a
// server that keeps saying "more" without moving on.
export const CONTINUE_OCR_MAX_REQUESTS = 100;

export async function continueOcrExtractionInBatches(requestBatch, { onProgress } = {}) {
  let requests = 0;
  let anyUpdated = false;
  let additionalTextLength = 0;
  let previousRemaining = null;
  let last = {};
  let stopReason = null;

  for (;;) {
    last = (await requestBatch()) || {};
    requests += 1;
    if (last.updated) anyUpdated = true;
    additionalTextLength += Number(last.additional_text_length) || 0;
    const remaining = Number(last.remaining_pages) || 0;

    if (onProgress) {
      onProgress({
        pagesProcessed: last.pages_processed,
        totalPages: last.total_pages,
        remainingPages: remaining,
        requests,
      });
    }

    if (!last.has_more || remaining <= 0) {
      if (remaining > 0 && last.service_failure) stopReason = "service_failure";
      break;
    }
    // No progress since the previous batch: stop rather than loop forever.
    if (previousRemaining !== null && remaining >= previousRemaining) {
      stopReason = "no_progress";
      break;
    }
    if (requests >= CONTINUE_OCR_MAX_REQUESTS) {
      stopReason = "request_limit";
      break;
    }
    previousRemaining = remaining;
  }

  const remainingPages = Number(last.remaining_pages) || 0;
  return {
    ...last,
    updated: anyUpdated,
    additional_text_length: additionalTextLength,
    requests,
    // Pages still unprocessed because the loop was cut short: the OCR service
    // failed (`service_failure`), or the no-progress/request guard stopped a
    // server still saying `has_more`. The caller keeps its pending entry so
    // the user can pick up where it stopped. Blank last pages are NOT this:
    // the server answers those with has_more false and no service_failure,
    // and the old "No additional text extracted" outcome stands.
    incomplete:
      remainingPages > 0 && Boolean(last.service_failure || last.has_more),
    // Why an incomplete run stopped: "service_failure" | "no_progress" |
    // "request_limit" (null when it finished).
    stopReason: remainingPages > 0 ? stopReason : null,
    // Some batch moved the document forward (saved text or skipped pages).
    progressed: anyUpdated || requests > 1,
  };
}

// The toast for a run that stopped with pages left, or null when it finished.
// The wording follows WHY it stopped.
export function continueOcrIncompleteToast(result) {
  if (!result || !result.incomplete) return null;
  const left = Number(result.remaining_pages) || 0;
  const pages = `${left} page${left === 1 ? "" : "s"}`;
  const saved = result.progressed ? "Progress so far was saved. " : "";
  let why;
  if (result.stopReason === "request_limit") {
    why = `${pages} remain — stopped after ${result.requests} requests. ` +
      `Click "Process remaining pages" to continue.`;
  } else if (result.stopReason === "no_progress") {
    why = `${pages} remain — the server stopped making progress on this document. ` +
      `Try "Process remaining pages" again later.`;
  } else {
    why = `${pages} remain — the text service did not respond. ` +
      `Try "Process remaining pages" again later.`;
  }
  return {
    title: "Some pages are not processed yet",
    description: saved + why,
    status: "warning",
  };
}

// The pending-truncation entry to keep after an incomplete run, with the
// counts the server reported, so the prompt that comes back shows the pages
// that are actually left.
export function continueOcrUpdatedTruncation(truncationInfo, result) {
  const next = { ...(truncationInfo || {}) };
  if (!result) return next;
  const num = (v) => (v === undefined || v === null || v === "" ? null : Number(v));
  const processed = num(result.pages_processed);
  const remaining = num(result.remaining_pages);
  const total = num(result.total_pages);
  if (processed !== null && !Number.isNaN(processed)) next.pages_processed = processed;
  if (remaining !== null && !Number.isNaN(remaining)) next.remaining_pages = remaining;
  if (total) next.total_pages = total;
  return next;
}

// "Processing... 20 of 35 pages" for the button while batches run.
export function continueOcrProgressLabel(progress) {
  if (!progress || !progress.totalPages) return "Processing...";
  return `Processing... ${progress.pagesProcessed || 0} of ${progress.totalPages} pages`;
}
